// ─── AddressFieldShared ─────────────────────────────────────────────
// Campo de morada com geocoding multi-provider (Google Places + Mapbox
// + HERE + OSM) extraído do OrderForm.js para ser reusado por outros
// dialogs (ex.: FuelOrderWizard). Mantém a UX igual: digita 3+ chars,
// sugestões aparecem com tag da fonte, clica para escolher → preenche
// nome + coordenadas.
//
// API:
//   <AddressFieldShared
//     label="Morada"
//     value="..."
//     onChange={text => ...}
//     onSelect={item => ...}   // item.coordinates = [lng, lat]
//     country="PT"             // default: PT
//   />
import React, { useState, useRef } from 'react';
import { Box, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { geocodeSearchMulti } from '../../utils/mapboxApi';

const useStyles = makeStyles(() => ({
  wrap: { position: 'relative', width: '100%' },
  suggestions: {
    position: 'absolute',
    top: '100%', left: 0, right: 0,
    background: '#fff',
    border: '1px solid #e2e8f0',
    borderRadius: 6,
    boxShadow: '0 6px 20px rgba(15,23,42,0.12)',
    zIndex: 1500,
    maxHeight: 220,
    overflowY: 'auto',
    marginTop: 2,
  },
  item: {
    padding: '8px 10px',
    fontSize: '0.78rem',
    cursor: 'pointer',
    borderBottom: '1px solid #f1f5f9',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    '&:hover': { background: '#f8fafc' },
    '&:last-child': { borderBottom: 'none' },
  },
  tag: {
    fontSize: '0.52rem', fontWeight: 800,
    padding: '1px 6px', borderRadius: 999,
    letterSpacing: '0.04em', textTransform: 'uppercase',
    flexShrink: 0,
  },
}));

const SRC_CFG = {
  'google-geo': { label: 'Morada', color: '#15803d', bg: 'rgba(22,163,74,0.12)' },
  google: { label: 'Places', color: '#1a73e8', bg: 'rgba(26,115,232,0.10)' },
  mapbox: { label: 'Mapbox', color: '#475569', bg: 'rgba(71,85,105,0.10)' },
  here:   { label: 'HERE',   color: '#0ea5e9', bg: 'rgba(14,165,233,0.10)' },
  osm:    { label: 'OSM',    color: '#16a34a', bg: 'rgba(22,163,74,0.10)' },
};

function AddressFieldShared({ label, value, onChange, onSelect, country = 'PT', size = 'small', helperText }) {
  const classes = useStyles();
  const [suggestions, setSuggestions] = useState([]);
  const [focused, setFocused] = useState(false);
  const debounceRef = useRef(null);

  function handleChange(e) {
    const val = e.target.value;
    onChange(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (val.length >= 3) {
        try {
          const results = await geocodeSearchMulti(val, { country });
          setSuggestions(results || []);
        } catch { setSuggestions([]); }
      } else {
        setSuggestions([]);
      }
    }, 350);
  }

  function handleSelect(item) {
    onChange(item.name);
    onSelect(item);
    setSuggestions([]);
  }

  return (
    <Box className={classes.wrap}>
      <TextField
        fullWidth
        variant="outlined"
        size={size}
        label={label}
        value={value}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 200)}
        helperText={helperText}
      />
      {focused && suggestions.length > 0 && (
        <Box className={classes.suggestions}>
          {suggestions.map((s, i) => {
            const cfg = SRC_CFG[s.source] || null;
            return (
              <Box
                key={i}
                className={classes.item}
                onMouseDown={() => handleSelect(s)}
              >
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {s.name}
                </span>
                {cfg && (
                  <span className={classes.tag} style={{ color: cfg.color, background: cfg.bg }}>
                    {cfg.label}
                  </span>
                )}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default AddressFieldShared;
